'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { HomePageConfig, STYLE_VARIANTS } from '@/lib/home-config-types';

const DesignEveryCorner = dynamic(() => import('../sections/design-every-corner').then(mod => ({ default: mod.DesignEveryCorner })));
const CuratedDesignCollections = dynamic(() => import('../sections/curated-design-collections').then(mod => ({ default: mod.CuratedDesignCollections })));
const ExpertCuratedCollections = dynamic(() => import('../sections/expert-curated-collections').then(mod => ({ default: mod.ExpertCuratedCollections })));
const TrendingNow = dynamic(() => import('../sections/trending-now').then(mod => ({ default: mod.TrendingNow })));
const SpecialOffers = dynamic(() => import('../sections/special-offers').then(mod => ({ default: mod.SpecialOffers })));
const VirtualShoppingExperience = dynamic(() => import('../sections/virtual-shopping-experience').then(mod => ({ default: mod.VirtualShoppingExperience })));
const HowHinchWorks = dynamic(() => import('../sections/how-hinch-works').then(mod => ({ default: mod.HowHinchWorks })));
const WhyChooseHinch = dynamic(() => import('../sections/why-choose-hinch').then(mod => ({ default: mod.WhyChooseHinch })));
const SolutionsForProfessionals = dynamic(() => import('../sections/solutions-for-professionals').then(mod => ({ default: mod.SolutionsForProfessionals })));
const Testimonials = dynamic(() => import('../sections/testimonials').then(mod => ({ default: mod.Testimonials })));
const ExperienceCenter = dynamic(() => import('../sections/experience-center').then(mod => ({ default: mod.ExperienceCenter })));
const ShopByCategory = dynamic(() => import('../sections/shop-by-category').then(mod => ({ default: mod.ShopByCategory })));

interface Props {
  config: HomePageConfig;
}

/**
 * Main component that renders all configurable home page sections
 * Each section is fully driven by JSON configuration
 */
export function ConfigurableSections({ config }: Props) {
  if (!config || !config.sections || !Array.isArray(config.sections)) {
    console.error('Invalid config provided to ConfigurableSections');
    return null;
  }

  return (
    <>
      {config.sections.map((section, index) => {
        // Validate section structure
        if (!section || typeof section !== 'object') {
          console.warn(`Section at index ${index} is invalid:`, section);
          return null;
        }

        if (!section.type) {
          console.warn(`Section at index ${index} missing required 'type' field:`, section);
          return null;
        }

        if (!section.style) {
          console.warn(`Section at index ${index} missing required 'style' field:`, section);
          return null;
        }

        if (!section.items || !Array.isArray(section.items)) {
          console.warn(`Section at index ${index} missing or invalid 'items' array:`, section);
          return null;
        }

        // Validate style variant
        const validStyles = STYLE_VARIANTS[section.type];
        if (validStyles && !validStyles.includes(section.style as never)) {
          console.warn(
            `Unknown style "${section.style}" for section type "${section.type}". ` +
            `Valid styles are: ${validStyles.join(', ')}`
          );
          return null;
        }

        // Render appropriate section component
        try {
          let sectionComponent;
          switch (section.type) {
            case 'design-every-corner':
              sectionComponent = <DesignEveryCorner key={`${section.type}-${index}`} config={section} />;
              break;
            
            case 'curated-design-collections':
              sectionComponent = <CuratedDesignCollections key={`${section.type}-${index}`} config={section} />;
              break;
            
            case 'expert-curated-collections':
              sectionComponent = <ExpertCuratedCollections key={`${section.type}-${index}`} config={section} />;
              break;
            
            case 'trending-now':
              sectionComponent = (
                <React.Fragment key={`${section.type}-${index}`}>
                  <TrendingNow config={section} />
                  <VirtualShoppingExperience />
                  <HowHinchWorks />
                  <WhyChooseHinch />
                  <SolutionsForProfessionals />
                  <Testimonials />
                  <ExperienceCenter />
                </React.Fragment>
              );
              break;
            
            case 'special-offers':
              sectionComponent = <SpecialOffers key={`${section.type}-${index}`} config={section} />;
              break;
            
            case 'shop-by-category':
              sectionComponent = <ShopByCategory key={`${section.type}-${index}`} config={section} />;
              break;
           
            default:
              console.warn(`Unknown section type: ${section.type}`);
              return null;
          }
          return sectionComponent;
        } catch (error) {
          console.error(`Error rendering section at index ${index}:`, error);
          return null;
        }
      })}
    </>
  );
}

